import { ProblemDetailsSchema, type ProblemDetails } from '@lectura/ts-contracts';
import type { z } from 'zod';
import { authFetch } from './auth';

// `/api/<svc>/...` Next.js rewrite-on át megy a microservice-re.
// A queries.ts pl. '/publications' → ténylegesen '/api/catalog/publications'.
const API_BASE = '/api/catalog';

/**
 * Microservice-ek prefix-térképe: a hívó `service: 'analytics'`-et ír,
 * az URL-építő `/api/analytics/...`-té oldja fel. Default: `catalog`.
 */
const SERVICE_PREFIX: Record<string, string> = {
  catalog: '/api/catalog',
  identity: '/api/identity',
  ingestion: '/api/ingestion',
  manifest: '/api/manifest',
  reader: '/api/reader',
  validation: '/api/validation',
  conversion: '/api/conversion',
  analytics: '/api/analytics',
  audit: '/api/audit',
  notification: '/api/notification',
  integration: '/api/integration',
};

export type ServiceName = keyof typeof SERVICE_PREFIX;

export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails, public readonly status: number) {
    super(problem.title);
    this.name = 'ApiError';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Megadhatja a célzott szervízt; ha nincs, a `catalog` default érvényes. */
  service?: ServiceName;
}

function buildUrl(path: string, query?: RequestOptions['query'], service?: ServiceName) {
  // `new URL(relative)` egyetlen-arg hívással `TypeError`-t dob — kell base.
  const isAbsolute = path.startsWith('http');
  const prefix = service ? SERVICE_PREFIX[service] : API_BASE;
  const fullPath = isAbsolute ? path : `${prefix}${path}`;
  const base =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'http://localhost';
  const url = new URL(fullPath, base);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return isAbsolute ? url.toString() : `${url.pathname}${url.search}`;
}

/** snake_case → camelCase rekurzív kulcsátnevezés (a Zod camelCase-t vár). */
function snakeToCamelKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snakeToCamelKeys);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const camelKey = k.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
      out[camelKey] = snakeToCamelKeys(v);
    }
    return out;
  }
  return value;
}

/** camelCase → snake_case rekurzív kulcsátnevezés (a Pydantic backend
 *  snake_case-t vár a request body-kban). */
export function camelToSnakeKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camelToSnakeKeys);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const snakeKey = k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
      out[snakeKey] = camelToSnakeKeys(v);
    }
    return out;
  }
  return value;
}

export async function api<T extends z.ZodTypeAny>(
  schema: T,
  path: string,
  options: RequestOptions = {},
): Promise<z.infer<T>> {
  const { body, query, headers, service, ...rest } = options;
  const traceId = crypto.randomUUID();
  const res = await authFetch(buildUrl(path, query, service), {
    ...rest,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Trace-Id': traceId,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    // A Pydantic backend snake_case kulcsokat vár; a frontend camelCase-ben
    // dolgozik. Itt egységesen lemeneteljük, hogy minden `api()` hívás helyes
    // body-t küldjön (pl. `publicationId` → `publication_id`).
    body: body !== undefined ? JSON.stringify(camelToSnakeKeys(body)) : undefined,
  });

  if (!res.ok) {
    let problem: ProblemDetails;
    try {
      problem = ProblemDetailsSchema.parse(await res.json());
    } catch {
      problem = {
        type: 'about:blank',
        title: res.statusText,
        status: res.status,
        traceId,
      };
    }
    throw new ApiError(problem, res.status);
  }
  if (res.status === 204) return undefined as z.infer<T>;
  const data = await res.json();
  const camel = snakeToCamelKeys(data);
  const parsed = schema.safeParse(camel);
  if (!parsed.success) {
    // eslint-disable-next-line no-console
    console.error('[lectura/admin] Zod parse fail', {
      path,
      issues: parsed.error.issues,
      received: camel,
    });
    throw new ApiError(
      {
        type: 'about:blank',
        title: 'Schema parse hiba',
        status: 200,
        detail: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
        traceId,
      },
      200,
    );
  }
  return parsed.data;
}
